import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import '../../scss/match/editMatchModal.scss';
import api from '../../services/apiService';

const EditTrainingModal = ({ match, onClose, onMatchUpdated }) => {
    const [message, setMessage] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [status, setStatus] = useState('active'); // Always start with 'active' for new updates
    const [alertType, setAlertType] = useState(''); // 'all', 'yes', 'none', empty for required selection
    const [trainingDate, setTrainingDate] = useState(match.match_date || '');
    const [trainingTime, setTrainingTime] = useState(match.match_time || '19:30');
    const [location, setLocation] = useState(match.pitch_id || '');

    const [duration, setDuration] = useState(match.duration || 60);
    const [pitches, setPitches] = useState([]);

    const [statusMessage, setStatusMessage] = useState(''); // Always start with empty message for new updates
    const [responseMessage, setResponseMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);
    const [instructions, setInstructions] = useState(match.instructions || '');
    const [isTBC, setIsTBC] = useState(match.status === 'TBC');

    useEffect(() => {
        const fetchPitches = async () => {
            try {
                const response = await api.get(`${process.env.REACT_APP_API_URL}/fetch_pitches.php`);
                const result = response.data;
                if (result.success) {
                    setPitches(result.pitches);
                }
            } catch (error) {
                console.error('Error fetching pitches:', error);
            }
        };

        fetchPitches();
    }, []);

    // Set the current pitch_id when pitches are loaded
    useEffect(() => {
        if (pitches.length > 0 && match.pitch_id) {
            // Find the current pitch and set the location state
            const currentPitch = pitches.find(pitch => pitch.id === match.pitch_id);
            if (currentPitch) {
                setLocation(match.pitch_id.toString());
            }
        }
    }, [pitches, match.pitch_id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate that alert type is selected
        if (!alertType) {
            setResponseMessage('Please select an alert option before saving.');
            setShowMessage(true);
            return;
        }
        
        // Validate that status message is provided when alerts are enabled or when time/venue changes
        if ((alertType !== 'none' || status === 'time_changed' || status === 'venue_changed' || status === 'venue_time_changed') && !statusMessage.trim()) {
            setResponseMessage('Please provide an update message when sending alerts to players or when changing time/venue.');
            setShowMessage(true);
            return;
        }
        
        // Note: Removed validation that forced alerts for time/venue changes
        // Users can now select "Don't Alert Anyone" and still save changes
        
        setIsUpdating(true);

        try {
            // Find the selected pitch name for the location field
            let pitchName = '';
            if (pitches && pitches.length > 0 && location) {
                let selectedPitch;
                
                                    // First try to find by ID
                    if (location && location !== '') {
                        selectedPitch = pitches.find(pitch => pitch && pitch.pitch_id && pitch.pitch_id.toString() === location);
                    }
                
                // If not found by ID, try by index (fallback)
                if (!selectedPitch && !isNaN(location)) {
                    const index = parseInt(location);
                    if (index >= 0 && index < pitches.length) {
                        selectedPitch = pitches[index];
                    }
                }
                
                if (selectedPitch) {
                    pitchName = `${selectedPitch.name} - ${selectedPitch.town ?? ''} ${selectedPitch.county ?? ''}`.trim();
                }
            }
            
            // Fallback to current match location if no pitch found
            if (!pitchName && match.location) {
                pitchName = match.location;
            }
            
            const response = await api.post(`${process.env.REACT_APP_API_URL}/update_match.php`, {
                match_id: match.match_id,
                match_date: trainingDate,
                match_time: trainingTime,
                pitch_id: parseInt(location),  // Convert to integer for database
                location: pitchName,
                duration,
                message: statusMessage,
                status: isTBC ? 'TBC' : status,
                instructions,
                alert_type: alertType,
                type: 'training',
                isTBC: isTBC
            });

            const result = response.data;
            
            if (result.success) {
                // Find the selected pitch name for the location field
                let updatedLocation = '';
                if (pitches && pitches.length > 0 && location) {
                    let selectedPitch;
                    
                    // First try to find by ID
                    if (location && location !== '') {
                        selectedPitch = pitches.find(pitch => pitch && pitch.pitch_id && pitch.pitch_id.toString() === location);
                    }
                    
                    // If not found by ID, try by index (fallback)
                    if (!selectedPitch && !isNaN(location)) {
                        const index = parseInt(location);
                        if (index >= 0 && index < pitches.length) {
                            selectedPitch = pitches[index];
                        }
                    }
                    
                    if (selectedPitch) {
                        updatedLocation = `${selectedPitch.name} - ${selectedPitch.town ?? ''} ${selectedPitch.county ?? ''}`.trim();
                    }
                }
                
                // Fallback to current match location if no pitch found
                if (!updatedLocation && match.location) {
                    updatedLocation = match.location;
                }
                
                onMatchUpdated({
                    ...match,
                    match_date: trainingDate,
                    match_time: trainingTime,
                    pitch_id: parseInt(location),  // Convert to integer for database
                    location: updatedLocation,
                    duration,
                    status,
                    last_update_message: statusMessage,
                    instructions
                });
                
                setResponseMessage('Training session updated successfully');
                setShowMessage(true);
                setTimeout(() => {
                    onClose();
                    window.location.reload(); // Uncommented - page will refresh after successful update
                }, 1500);
            } else {
                setResponseMessage('Failed to update training: ' + result.message);
                setShowMessage(true);
            }
        } catch (error) {
            console.error('Error updating training:', error);
            setResponseMessage('Error updating training session. Please try again.');
            setShowMessage(true);
        } finally {
            setIsUpdating(false);
        }
    };

    // Use createPortal to render the modal to document.body
    return createPortal(
        <div className="modal-overlay EditMatchModal" onClick={onClose}>
            <div className="modal-content modal" onClick={e => e.stopPropagation()}>
                <h2>Edit Training Session</h2>
                <button type="button" className="modal-close" onClick={onClose}>
                    <FaTimes />
                </button>
                <form onSubmit={handleSubmit}>

                    
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="trainingDate">Training Date:</label>
                            <input
                                type="date"
                                id="trainingDate"
                                value={trainingDate}
                                onChange={(e) => setTrainingDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="trainingTime">Start Time:</label>
                            <input
                                type="time"
                                id="trainingTime"
                                value={isTBC ? "00:00" : trainingTime}
                                onChange={(e) => setTrainingTime(e.target.value)}
                                disabled={isTBC}
                                required={!isTBC}
                                style={{ opacity: isTBC ? 0.2 : 1 }}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={isTBC}
                                    onChange={(e) => {
                                        setIsTBC(e.target.checked);
                                        if (e.target.checked) {
                                            setTrainingTime("00:00");
                                        }
                                    }}
                                />
                                Start Time TBC (To Be Confirmed)
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="location">Location:</label>
                        <select
                            id="location"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                        >
                            <option value="">Select Location</option>
                            {pitches.map((pitch, index) => (
                                <option key={pitch?.pitch_id || index} value={pitch?.pitch_id || index}>
                                    {pitch?.name || 'Unknown'} - {pitch?.town ?? ''} {pitch?.county ?? ''}
                                </option>
                            ))}
                        </select>
                    </div>

                                                <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="duration">Duration (mins):</label>
                                    <input
                                        type="number"
                                        id="duration"
                                        value={duration}
                                        onChange={(e) => setDuration(parseInt(e.target.value))}
                                        min="1"
                                    />
                                </div>

                            </div>
                    
                                        <div className="form-group">
                        <label htmlFor="instructions">Instructions:</label>
                        <textarea
                            id="instructions"
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            placeholder="Add training instructions..."
                            rows="4"
                        />
                    </div>

                    <div className="notification-section" style={{
                        marginTop: '2rem',
                        padding: '1.5rem',
                        backgroundColor: 'rgba(10, 179, 216, 0.1)',
                        border: '1px solid rgba(10, 179, 216, 0.3)',
                        borderRadius: '8px',
                        borderLeft: '4px solid #0ab3d8'
                    }}>
                        <h3 className="section-heading" style={{
                            margin: '0 0 0.5rem 0',
                            color: '#0ab3d8',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>Notification Settings</h3>
                        <p className="section-description" style={{
                            margin: '0 0 1.5rem 0',
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.9rem',
                            fontStyle: 'italic'
                        }}>Configure how to notify players about these changes</p>
                        
                        <div className="form-group">
                            <label>Status:</label>
                            <select 
                                value={status} 
                                onChange={(e) => setStatus(e.target.value)}
                                disabled={isUpdating}
                            >
                                <option value="active">Active - Training is on!</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="postponed">Postponed</option>
                                <option value="time_changed">Change of Time</option>
                                <option value="venue_changed">Change of Venue</option>
                                <option value="venue_time_changed">Venue & Time Changed</option>
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="statusMessage">
                                Update Message {(alertType && alertType !== 'none') || status === 'time_changed' || status === 'venue_changed' || status === 'venue_time_changed' ? '(required)' : '(optional)'}:
                            </label>
                            <textarea
                                id="statusMessage"
                                value={statusMessage}
                                onChange={(e) => setStatusMessage(e.target.value)}
                                placeholder={(alertType && alertType !== 'none') || status === 'time_changed' || status === 'venue_changed' || status === 'venue_time_changed' ? 
                                    "Please describe what has changed (e.g., 'Location changed to Hastings Sports Centre', 'Time changed to 7:00 PM')" : 
                                    "Add a status message..."
                                }
                                rows="4"
                                required={(alertType && alertType !== 'none') || status === 'time_changed' || status === 'venue_changed' || status === 'venue_time_changed'}
                            />
                        </div>

                        <div className="form-group">
                            <label>Alert Players:</label>
                            <select 
                                value={alertType} 
                                onChange={(e) => setAlertType(e.target.value)}
                                required
                            >
                                <option value="">-- Please Select --</option>
                                <option value="none">Don't Alert Anyone</option>
                                <option value="yes">Alert Players Attending</option>
                                <option value="yes_awaiting">Alert Players Attending + Awaiting</option>
                                <option value="all">Alert All Invited Players</option>
                            </select>
                            {(status === 'time_changed' || status === 'venue_changed' || status === 'venue_time_changed') && (
                                <small style={{ 
                                    color: '#ffc107', 
                                    fontStyle: 'italic', 
                                    marginTop: '0.5rem',
                                    display: 'block'
                                }}>
                                    💡 Tip: Time and venue changes are important updates. Consider alerting all invited players.
                                </small>
                            )}
                        </div>
                    </div>

                    <div className="form-navigation">
                        <button type="button" onClick={onClose} className="back-stage-button">
                            Cancel
                        </button>
                        <button type="submit" className="next-stage-button" disabled={isUpdating}>
                            {isUpdating ? 'Updating...' : 'Save Changes'}
                        </button>
                    </div>
                </form>

                {showMessage && (
                    <div className="response-message">
                        {responseMessage}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default EditTrainingModal; 